import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  emissionAllocationConfig,
  gatheringConfig,
  globalInputsConfig,
  liquefactionConfig,
  processingConfig,
  productionConfig,
  shippingConfig,
  shippingDistance,
  transmissionConfig,
} from './formConfig';
import { iniToJson } from './iniToJson';

interface FormData {
  [key: string]: any;
}

interface FormCompletionStatus {
  [formKey: string]: boolean;
}

interface FormContextType {
  formData: FormData;
  formCompletionStatus: FormCompletionStatus;
  allFormsCompleted: boolean;
  basin: string;
  liquefaction: string;
  iniJsonData: Record<string, any>;
  processIniFiles: (result: any) => Promise<void>;
  updateField: (
    formKey: string,
    fieldKey: string,
    value: any,
    withDefault?: boolean,
  ) => void;
}

const FormContext = createContext<FormContextType | undefined>(undefined);

const configs = [
  { key: 'globalInputsForm', config: globalInputsConfig },
  { key: 'productionForm', config: productionConfig },
  { key: 'gatheringForm', config: gatheringConfig },
  { key: 'processingForm', config: processingConfig },
  { key: 'transmissionForm', config: transmissionConfig },
  { key: 'liquefactionForm', config: liquefactionConfig },
  { key: 'shippingForm', config: shippingConfig },
  { key: 'emissionAllocationForm', config: emissionAllocationConfig },
];

const isEqual = require('lodash.isequal');

const initializeFormData = () => {
  const formData: FormData = {};
  const formCompletionStatus: FormCompletionStatus = {};

  configs.forEach(({ key, config }) => {
    formData[key] = {};
    formCompletionStatus[key] = false;
    if (config.key) {
      formData[key][config.key] = '';
    }
    Object.keys(config.sectionsByOption).forEach((option) => {
      config.sectionsByOption[option].forEach((section) => {
        section.fields.forEach((field) => {
          const fieldKey =
            field.key || field.label.replace(/[^a-zA-Z0-9]/g, '_');
          formData[key][fieldKey + '_readonly'] = !!field.readonly;
          formData[key][fieldKey] = '';
        });
      });
    });
  });

  return { formData, formCompletionStatus };
};

export const FormProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const {
    formData: initialFormData,
    formCompletionStatus: initialCompletionStatus,
  } = initializeFormData();
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [formCompletionStatus, setFormCompletionStatus] =
    useState<FormCompletionStatus>(initialCompletionStatus);
  const [allFormsCompleted, setAllFormsCompleted] = useState<boolean>(false);

  const [iniJsonData, setIniJsonData] = useState<Record<string, any>>({});
  const [basin, setBasin] = useState('');
  const [liquefaction, setLiquefaction] = useState('');

  const processIniFiles = async (result: any) => {
    const results: Record<string, any> = {};

    Object.entries(result).forEach((entry: [any, any]) => {
      try {
        const fileName = entry[0].split('.');
        if (fileName.length > 0) {
          results[fileName[0]] = iniToJson(entry[1] || '');
        }
      } catch (error) {
        console.error(`Error "${entry[0]}":`, error);
      }
    });

    setIniJsonData(results);
  };

  const checkFormCompletion = (formKey: string, formFields: any) => {
    const missingFields: string[] = [];

    Object.entries(formFields).forEach(([fieldKey, fieldValue]) => {
      if (
        fieldValue === null ||
        fieldValue === '' ||
        fieldValue === undefined
      ) {
        missingFields.push(fieldKey);
      }
    });

    return {
      isComplete: missingFields.length === 0,
      missingFields,
    };
  };

  const getDefaultValue = (formKey: string, fieldKey: string): any => {
    const config = configs.find((c) => c.key === formKey)?.config;
    if (!config) return '';

    for (const option of Object.keys(config.sectionsByOption)) {
      for (const section of config.sectionsByOption[option]) {
        for (const field of section.fields) {
          const currentFieldKey =
            field.key || field.label.replace(/[^a-zA-Z0-9]/g, '_');
          if (currentFieldKey === fieldKey) {
            return field.value || '';
          }
        }
      }
    }

    return '';
  };

  const updateField = (
    formKey: string,
    fieldKey: string,
    value: any,
    withDefault = false,
  ) => {
    setFormData((prev) => {
      const finalValue =
        value.trim() === '' ? getDefaultValue(formKey, fieldKey) : value;

      if (prev[formKey]?.[fieldKey] === finalValue) {
        return prev;
      }

      const updatedForm = {
        ...prev[formKey],
        [fieldKey]: withDefault ? finalValue : value,
      };

      return {
        ...prev,
        [formKey]: updatedForm,
      };
    });
  };

  useEffect(() => {
    setBasin(formData.globalInputsForm.production_basin.toLowerCase());
    if (formData.globalInputsForm.production_basin === 'User-Defined') {
      const updatedFormData = { ...formData };

      Object.keys(updatedFormData).forEach((formKey) => {
        if (
          ![
            'globalInputsForm',
            'liquefactionForm',
            'shippingForm',
            'emissionAllocationForm',
          ].includes(formKey)
        ) {
          const formSection = updatedFormData[formKey];

          if (typeof formSection === 'object' && formSection !== null) {
            updatedFormData[formKey] = Object.keys(formSection).reduce(
              (acc, key) => {
                const typedAcc = acc as Record<string, string>;

                const isReadonly = formSection[key + '_readonly'] === true;

                if (isReadonly) {
                  typedAcc[key] = formSection[key];
                } else if (!key.endsWith('_readonly')) {
                  typedAcc[key] = '';
                }

                return typedAcc;
              },
              {} as Record<string, string>,
            );
          }
        }
      });

      if (!isEqual(updatedFormData, formData)) {
        setFormData(updatedFormData);
      }
    }
    if (
      formData.globalInputsForm.production_basin !== '' &&
      formData.globalInputsForm.production_basin !== 'User-Defined'
    ) {
      const updatedFormData = { ...formData };

      configs.forEach(({ key, config }) => {
        if (key !== 'globalInputsForm' && key !== 'liquefactionForm') {
          const defaultValues: Record<string, any> = {};

          if (config.key) {
            updatedFormData[key][config.key] = config.value || '';
          }

          Object.keys(config.sectionsByOption).forEach((option) => {
            config.sectionsByOption[option].forEach((section) => {
              section.fields.forEach((field) => {
                const fieldKey =
                  field.key || field.label.replace(/[^a-zA-Z0-9]/g, '_');
                if (
                  formData.globalInputsForm.production_basin !==
                    'User-Defined' &&
                  fieldKey !== 'shipping.shipping_distance' &&
                  Object.keys(
                    iniJsonData[
                      formData.globalInputsForm.production_basin.toLowerCase()
                    ],
                  ).length > 0
                ) {
                  let val;
                  const valueFromConfigRaw = JSON.stringify(
                    iniJsonData[
                      formData.globalInputsForm.production_basin.toLowerCase()
                    ],
                  ).split(fieldKey.split('.')[1]);

                  if (valueFromConfigRaw.length > 1) {
                    const valueFromConfigWithUnits =
                      valueFromConfigRaw[1].split(',')[0];

                    const valueFromConfig =
                      valueFromConfigWithUnits.indexOf('|') > 0
                        ? valueFromConfigWithUnits.split('|')[0]
                        : valueFromConfigWithUnits;

                    if (valueFromConfig) {
                      const match = valueFromConfig.match(/\d+(\.\d+)?/);
                      if (match && match.length > 0) {
                        val = match[0];
                      }
                    }
                  }

                  defaultValues[fieldKey] = val || field.value || '';
                }
              });
            });
          });

          if (updatedFormData[key]) {
            Object.keys(defaultValues).forEach((fieldKey) => {
              // if (updatedFormData[key][fieldKey] === '') {
              updatedFormData[key][fieldKey] = defaultValues[fieldKey];
              // }
            });
          }
        }
      });

      if (!isEqual(updatedFormData, formData)) {
        setFormData(updatedFormData);
      }
    }
  }, [formData.globalInputsForm.production_basin]);

  useEffect(() => {
    if (
      formData.globalInputsForm.liquefaction_facility === '' ||
      !formData.globalInputsForm.liquefaction_facility
    ) {
      return;
    }
    setLiquefaction(
      formData.globalInputsForm.liquefaction_facility.toLowerCase(),
    );
    const updatedFormData = { ...formData };
    configs.forEach(({ key, config }) => {
      if (key === 'liquefactionForm') {
        const defaultValues: Record<string, any> = {};

        if (config.key) {
          updatedFormData[key][config.key] = config.value || '';
        }

        Object.keys(config.sectionsByOption).forEach((option) => {
          config.sectionsByOption[option].forEach((section) => {
            section.fields.forEach((field) => {
              const fieldKey =
                field.key || field.label.replace(/[^a-zA-Z0-9]/g, '_');

              if (
                Object.keys(
                  iniJsonData['liquefaction_facilities'][
                    formData.globalInputsForm.liquefaction_facility.toLowerCase()
                  ],
                ).length > 0
              ) {
                let val;
                const valueFromConfigRaw = JSON.stringify(
                  iniJsonData['liquefaction_facilities'][
                    formData.globalInputsForm.liquefaction_facility.toLowerCase()
                  ],
                ).split(fieldKey.split('.')[1]);

                if (valueFromConfigRaw.length > 1) {
                  const valueFromConfigWithUnits =
                    valueFromConfigRaw[1].split(',')[0];

                  const valueFromConfig =
                    valueFromConfigWithUnits.indexOf('|') > 0
                      ? valueFromConfigWithUnits.split('|')[0]
                      : valueFromConfigWithUnits;

                  if (valueFromConfig) {
                    const match = valueFromConfig.match(/\d+(\.\d+)?/);
                    if (match && match.length > 0) {
                      val = match[0];
                    }
                  }
                }

                defaultValues[fieldKey] = val || field.value || '';
              }
              // defaultValues[fieldKey] = field.value || '';
            });
          });
        });

        if (updatedFormData[key]) {
          Object.keys(defaultValues).forEach((fieldKey) => {
            if (updatedFormData[key][fieldKey] === '') {
              updatedFormData[key][fieldKey] = defaultValues[fieldKey];
            }
          });
        }
      }
    });

    if (!isEqual(updatedFormData, formData)) {
      setFormData(updatedFormData);
    }
    checkFormCompletion('liquefactionForm', formData['liquefactionForm']);
  }, [formData.globalInputsForm.liquefaction_facility]);

  useEffect(() => {
    if (
      formData.globalInputsForm.shipping_destination === '' ||
      !formData.globalInputsForm.shipping_destination
    ) {
      return;
    }

    const updatedFormData = { ...formData };

    if (liquefaction && formData.globalInputsForm.shipping_destination) {
      const liquefactionKey = liquefaction as keyof typeof shippingDistance;
      const destinationKey = formData.globalInputsForm
        .shipping_destination as keyof (typeof shippingDistance)[typeof liquefactionKey];

      const distanceKey = Object.keys(iniJsonData['destinations']).find(
        (key) => {
          const city = destinationKey.split(',')[0].trim().toLowerCase();
          return key.includes(city);
        },
      );

      updatedFormData['shippingForm']['shipping.shipping_distance'] =
        distanceKey
          ? iniJsonData['destinations'][distanceKey][liquefactionKey]
          : shippingDistance[liquefactionKey][destinationKey];
    }

    if (!isEqual(updatedFormData, formData)) {
      setFormData(updatedFormData);
    }
  }, [
    formData.globalInputsForm.shipping_destination,
    formData.globalInputsForm.liquefaction_facility,
  ]);

  useEffect(() => {
    if (Object.entries(iniJsonData).length === 0) {
      return;
    }

    const updatedFormData = { ...formData };

    updatedFormData['shippingForm']['shipping.tanker_capacity'] =
      iniJsonData['tankers'][formData.shippingForm['shipping.lng_tanker_type']][
        'lng_tanker_capacity'
      ];

    updatedFormData['shippingForm'][
      'shipping.boiloff_generation_while_underway_from_generators'
    ] =
      iniJsonData['tankers'][formData.shippingForm['shipping.lng_tanker_type']][
        'generators_boil_off_rate_underway'
      ];

    updatedFormData['shippingForm'][
      'shipping.boiloff_generation_while_underway_from_propulsion'
    ] =
      iniJsonData['tankers'][formData.shippingForm['shipping.lng_tanker_type']][
        'propulsion_boil_off_rate_underway'
      ];

    updatedFormData['shippingForm'][
      'shipping.boiloff_generation_while_underway_from_GCU'
    ] =
      iniJsonData['tankers'][formData.shippingForm['shipping.lng_tanker_type']][
        'gcu_boil_off_generation_underway'
      ];

    updatedFormData['shippingForm'][
      'shipping.boiloff_generation_while_maneuvering_from_generators'
    ] =
      iniJsonData['tankers'][formData.shippingForm['shipping.lng_tanker_type']][
        'generators_boil_off_rate_manuvering'
      ];

    updatedFormData['shippingForm'][
      'shipping.boiloff_generation_while_maneuvering_from_propulsion'
    ] =
      iniJsonData['tankers'][formData.shippingForm['shipping.lng_tanker_type']][
        'propulsion_boil_off_rate_manuvering'
      ];

    updatedFormData['shippingForm'][
      'shipping.boiloff_generation_while_maneuvering_from_GCU'
    ] =
      iniJsonData['tankers'][formData.shippingForm['shipping.lng_tanker_type']][
        'gcu_boil_off_rate_manuvering'
      ];

    updatedFormData['shippingForm'][
      'shipping.boiloff_generation_while_docked_from_generators'
    ] =
      iniJsonData['tankers'][formData.shippingForm['shipping.lng_tanker_type']][
        'generators_boil_off_rate_docked'
      ];

    updatedFormData['shippingForm'][
      'shipping.boiloff_generation_while_docked_from_propulsion'
    ] =
      iniJsonData['tankers'][formData.shippingForm['shipping.lng_tanker_type']][
        'propulsion_boil_off_rate_docked'
      ];

    updatedFormData['shippingForm'][
      'shipping.boiloff_generation_while_docked_from_GCU'
    ] =
      iniJsonData['tankers'][formData.shippingForm['shipping.lng_tanker_type']][
        'gcu_boil_off_rate_docked'
      ];

    updatedFormData['shippingForm'][
      'shipping.methane_slip_while_underway_from_generators'
    ] =
      iniJsonData['tankers'][formData.shippingForm['shipping.lng_tanker_type']][
        'generators_methane_slip_ratio_underway'
      ];

    updatedFormData['shippingForm'][
      'shipping.methane_slip_while_underway_from_propulsion'
    ] =
      iniJsonData['tankers'][formData.shippingForm['shipping.lng_tanker_type']][
        'propulsion_methane_slip_ratio_underway'
      ];

    updatedFormData['shippingForm'][
      'shipping.methane_slip_while_underway_from_GCU'
    ] =
      iniJsonData['tankers'][formData.shippingForm['shipping.lng_tanker_type']][
        'gcu_methane_slip_ratio_underway'
      ];

    updatedFormData['shippingForm'][
      'shipping.methane_slip_while_maneuvering_from_generators'
    ] =
      iniJsonData['tankers'][formData.shippingForm['shipping.lng_tanker_type']][
        'generators_methane_slip_ratio_manuvering'
      ];

    updatedFormData['shippingForm'][
      'shipping.methane_slip_while_maneuvering_from_propulsion'
    ] =
      iniJsonData['tankers'][formData.shippingForm['shipping.lng_tanker_type']][
        'propulsion_methane_slip_ratio_manuvering'
      ];

    updatedFormData['shippingForm'][
      'shipping.methane_slip_while_maneuvering_from_GCU'
    ] =
      iniJsonData['tankers'][formData.shippingForm['shipping.lng_tanker_type']][
        'gcu_methane_slip_ratio_manuvering'
      ];

    updatedFormData['shippingForm'][
      'shipping.methane_slip_while_docked_from_generators'
    ] =
      iniJsonData['tankers'][formData.shippingForm['shipping.lng_tanker_type']][
        'generators_methane_slip_ratio_docked'
      ];

    updatedFormData['shippingForm'][
      'shipping.methane_slip_while_docked_from_propulsion'
    ] =
      iniJsonData['tankers'][formData.shippingForm['shipping.lng_tanker_type']][
        'propulsion_methane_slip_ratio_docked'
      ];

    updatedFormData['shippingForm'][
      'shipping.methane_slip_while_docked_from_GCU'
    ] =
      iniJsonData['tankers'][formData.shippingForm['shipping.lng_tanker_type']][
        'gcu_methane_slip_ratio_docked'
      ];

    updatedFormData['shippingForm'][
      'shipping.containment_technology_adjustment_factor'
    ] =
      iniJsonData['tankers'][formData.shippingForm['shipping.lng_tanker_type']][
        'containment_technology_adjustment_factor'
      ];

    updatedFormData['shippingForm']['shipping.surface_area_adjustment_factor'] =
      iniJsonData['tankers'][formData.shippingForm['shipping.lng_tanker_type']][
        'surface_area_adjustment_factor'
      ];

    updatedFormData['shippingForm'][
      'shipping.overall_underway_boiloff_generation'
    ] =
      iniJsonData['tankers'][formData.shippingForm['shipping.lng_tanker_type']][
        'overall_underway_boiloff_generation'
      ];

    updatedFormData['shippingForm'][
      'shipping.overall_maneuvering_boiloff_generation'
    ] =
      iniJsonData['tankers'][formData.shippingForm['shipping.lng_tanker_type']][
        'overall_maneuvering_boiloff_generation'
      ];

    updatedFormData['shippingForm'][
      'shipping.overall_docked_boiloff_generation'
    ] =
      iniJsonData['tankers'][formData.shippingForm['shipping.lng_tanker_type']][
        'overall_docked_boiloff_generation'
      ];

    if (!isEqual(updatedFormData, formData)) {
      setFormData(updatedFormData);
    }
  }, [formData.shippingForm['shipping.lng_tanker_type']]);

  useEffect(() => {
    const updatedFormData = { ...formData };
    updatedFormData['emissionAllocationForm'][
      'allocation_methods.emission_allocation_method'
    ] =
      formData.globalInputsForm.allocation_method_selection ===
      'First Allocation Method'
        ? 'Fully assigned to LNG'
        : 'Energy-based emission allocation';
  }, [formData.globalInputsForm.allocation_method_selection]);

  useEffect(() => {
    const updatedCompletionStatus: FormCompletionStatus = {};

    Object.keys(formData).forEach((formKey) => {
      const { isComplete } = checkFormCompletion(formKey, formData[formKey]);
      updatedCompletionStatus[formKey] = isComplete;
    });

    if (
      JSON.stringify(updatedCompletionStatus) !==
      JSON.stringify(formCompletionStatus)
    ) {
      setFormCompletionStatus(updatedCompletionStatus);
    }
  }, [
    formData,
    formData.globalInputsForm.liquefaction_facility,
    formData.globalInputsForm.production_basin,
  ]);

  useEffect(() => {
    const allCompleted = Object.values(formCompletionStatus).every(
      (status) => status,
    );
    setAllFormsCompleted(allCompleted);
  }, [formCompletionStatus]);

  const contextValue = useMemo(() => {
    return {
      formData,
      formCompletionStatus,
      allFormsCompleted,
      iniJsonData,
      processIniFiles,
      updateField,
      basin,
      liquefaction,
    };
  }, [
    formData,
    formCompletionStatus,
    allFormsCompleted,
    iniJsonData,
    basin,
    liquefaction,
  ]);

  return (
    <FormContext.Provider value={contextValue}>{children}</FormContext.Provider>
  );
};

export const useFormContext = (): FormContextType => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useFormContext must be used within a FormProvider');
  }
  return context;
};
